import { ILogger, MULTI_VIDEO_DATA_TYPE, RoomEvent, VideoCodec } from 'trtc-js-sdk-core';
import { Room } from '../room';
import { EventEmitter } from 'eventemitter3';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import { INNER_EVENT } from 'trtc-js-sdk-core/src/inner-emitter/inner-type';
import { InnerEventTypes } from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import { IS_ANDROID, IS_CHROMIUM_BASE } from 'trtc-js-sdk-core/src/utils/environment';

export enum HealthDetectorEvent {
  BAD_HEALTH = '1'
}
interface HealthDetectorEventTypes {
  [HealthDetectorEvent.BAD_HEALTH]: [{ isAux: boolean; }];
}
/**
 * 视频健康度检测器
 *
 * 当开启硬编的情况下，sdk 会检测视频的编码码率健康度和帧率健康度，在帧率健康度达标 90%+的情况下，如果码率健康度持续 6s 低于 50%，则切换至软编。
 * @author rychouwei
 */
class HealthDetector extends EventEmitter<HealthDetectorEventTypes> {
  static EVENT_BAD_HEALTH = 'bad_health';
  mainFpsHealth = 1;
  mainBitrateHealth = 1;
  badMainBitrateHealthCount = 0;
  lastEmitBadHealthTime = 0;
  log: ILogger;
  constructor(public room: Room) {
    super();
    if (!IS_ANDROID && IS_CHROMIUM_BASE) {
      innerEmitter.on(INNER_EVENT.VIDEO_CODEC_IMPLEMENTATION_CHANGED, this.onVideoCodecChanged, this);
    }
    this.log = room.getLogger().createChild({ id: 'h-d' });
  }

  onVideoCodecChanged({ remoteUserId, streamType, isHWCodec, codec }: InnerEventTypes[INNER_EVENT.VIDEO_CODEC_IMPLEMENTATION_CHANGED][0]) {
    if (remoteUserId || streamType === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG || codec !== VideoCodec.H264) return;
    if (!isHWCodec) {
      this.room.off(RoomEvent.HEARTBEAT_REPORT, this.onHeartbeatReport, this);
      return;
    }
    if (!this.room.listeners(RoomEvent.HEARTBEAT_REPORT).includes(this.onHeartbeatReport)) {
      this.room.on(RoomEvent.HEARTBEAT_REPORT, this.onHeartbeatReport, this);
    }
  }

  onHeartbeatReport(report: HearBeatReport) {
    // 抛出 bad health 后，间隔 30s 后再重新检测。
    if (Date.now() - this.lastEmitBadHealthTime < 30 * 1000) return;
    report.msg_up_stream_info.msg_video_status.forEach(item => {
      if (item.uint32_video_enc_fps && item.uint32_video_capture_fps) {
        const fpsHealth = item.uint32_video_enc_fps / item.uint32_video_capture_fps;
        if (item.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG) {
          this.mainFpsHealth = fpsHealth;
        }
      }
      if (item.uint32_video_codec_bitrate) {
        if (item.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG) {
          const { localMainVideoTrack } = this.room;
          if (localMainVideoTrack) {
            this.mainBitrateHealth = (item.uint32_video_codec_bitrate /  1000) / (localMainVideoTrack).profile.bitrate;
          }
        }
      }
    });

    this.log.debug(`mainBitrateHealth: ${this.mainBitrateHealth} mainFpsHealth: ${this.mainFpsHealth}`);
    if (this.mainBitrateHealth > 0.5) this.badMainBitrateHealthCount = 0;
    if (this.mainFpsHealth > 0.9 && this.mainBitrateHealth < 0.5) {
      this.badMainBitrateHealthCount++;
      if (this.badMainBitrateHealthCount > 3) {
        this.badMainBitrateHealthCount = 0;
        this.lastEmitBadHealthTime = Date.now();
        this.log.warn(`bad main bitrate health: ${this.mainBitrateHealth}`);
        this.emit(HealthDetectorEvent.BAD_HEALTH, { isAux: false });
      }
    }
  }

  destroy() {
    innerEmitter.off(INNER_EVENT.VIDEO_CODEC_IMPLEMENTATION_CHANGED, this.onVideoCodecChanged, this);
    this.room.off(RoomEvent.HEARTBEAT_REPORT, this.onHeartbeatReport, this);
  }
}

export default HealthDetector;
